import {
  DOMAttributes,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-slick';
import { useIntl } from 'react-intl';
import { RecommendationsActivityType, RecommendationsResponse } from 'src/api/me/types';
import { Button, Icon, LinkButton, LoadingSkeletonCard, LockedScenarioPopup } from 'src/components';
import { LIC_COLORS_DARK } from 'src/constants/lic.const';
import theme from 'src/utils/theme';
import useSafePush from 'src/hooks/useSafePush';
import { ROUTES } from 'src/constants/routes.const';
import { ActivitySource } from 'src/utils/analytics/analytics.const';
import { UserScenarioApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { Url } from 'next/dist/shared/lib/router/router';
import { useRouter } from 'next/router';
import { convertUrlToTriggerRefresh } from 'src/utils/convertUrlToTriggerRefresh';
import { useUserContext } from 'src/context/user';
import { msgs } from './messages';
import * as S from './styles';

import 'slick-carousel/slick/slick.css';

export type Activities = 'learn' | 'immerse' | 'communicate';

export type MiniPlayerProps = {
  currentActivity: RecommendationsActivityType;
  onChangeActivity: (activity: RecommendationsActivityType) => void;
  recommendations: RecommendationsResponse;
  supportedActivities: Activities[];
  source: ActivitySource;
  customImmerseRecommendation?: number;
  isLoading?: boolean;
};

type MiniPlayerContentProps = {
  icon: ReactNode;
  title: string;
  description: string;
  isEnabled: boolean;
  onStart: () => void;
};

const MiniPlayerContent: FC<MiniPlayerContentProps> = ({
  icon,
  title,
  description,
  isEnabled,
  onStart,
}) => {
  const intl = useIntl();
  const [descriptionScrollWidth, setDescriptionScrollWidth] = useState<number | undefined>();
  const [descriptionClientWidth, setDescriptionClientWidth] = useState<number | undefined>();
  const resizeObserverRef = useRef<ResizeObserver>(null);

  /* istanbul ignore next: tested visually */
  const onDescriptionRendered = useCallback((descriptionElement: HTMLDivElement | null) => {
    if (!descriptionElement) {
      resizeObserverRef.current?.disconnect();
      return;
    }

    resizeObserverRef.current = new ResizeObserver(() => {
      setDescriptionScrollWidth(descriptionElement.scrollWidth);
      setDescriptionClientWidth(descriptionElement.clientWidth);
    });

    resizeObserverRef.current.observe(descriptionElement);
  }, []);

  return (
    <S.Content dir={/* istanbul ignore next: tested visually */ theme.rtl ? 'rtl' : 'ltr'}>
      {icon}
      <S.MainContent>
        <S.Title data-testid="miniplayer_title_label">{title}</S.Title>
        <S.Description
          ref={onDescriptionRendered}
          $textLengthPx={descriptionScrollWidth}
          $visibleTextLengthPx={descriptionClientWidth}
        >
          {description}
        </S.Description>
      </S.MainContent>
      <S.Action data-testid="miniplayer_start_btn">
        <Button buttonType="primary" buttonSize="large" disabled={!isEnabled} onClick={onStart}>
          {intl.formatMessage(msgs.startCta)}
        </Button>
      </S.Action>
    </S.Content>
  );
};

export const MiniPlayerLearnActivity: FC<{
  recommendation?: RecommendationsResponse['learn'];
  source: ActivitySource;
}> = ({ recommendation, source }) => {
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const { safePush } = useSafePush();
  const { user } = useUserContext();

  const [isOpenLockedScenario, setIsOpenLockedScenario] = useState<boolean>(false);

  const [scenario, setScenario] = useState(recommendation?.nextScenario);
  const description = !scenario
    ? intl.formatMessage(msgs.disabledLearnDescription)
    : intl.formatMessage(
        scenario.progress.dateStarted
          ? msgs.startedScenarioDescription
          : msgs.notStartedScenarioDescription,
        {
          scenarioTitle: scenario.title,
        },
      );

  return (
    <>
      {scenario && (
        <LockedScenarioPopup
          isOpen={isOpenLockedScenario}
          onClose={() => setIsOpenLockedScenario(false)}
          scenario={scenario}
          onSkip={async () => {
            languagePairId &&
              (await UserScenarioApi.skipScenario(languagePairId, scenario.scenarioId, true));
            setScenario(recommendation?.nextFreeScenario);
            setIsOpenLockedScenario(false);
          }}
          skippable={!!recommendation?.nextFreeScenario}
        />
      )}
      <MiniPlayerContent
        icon={<S.ContentIcon name="learnFilled" $color={LIC_COLORS_DARK.learn} />}
        title={intl.formatMessage(msgs.learnTitle)}
        description={description}
        isEnabled={Boolean(scenario)}
        onStart={async () =>
          scenario?.isPremium && !user?.isPro && scenario?.isLocked
            ? setIsOpenLockedScenario(true)
            : await safePush(
                convertUrlToTriggerRefresh({
                  pathname: ROUTES.aprender.learn,
                  query: {
                    ...(scenario?.scenarioId && {
                      scenario_id: scenario.scenarioId,
                    }),
                    ...(scenario?.contentMediaId && {
                      contentMediaId: scenario?.contentMediaId,
                    }),
                    source,
                  },
                }),
              )
        }
      />
    </>
  );
};

export const MiniPlayerImmerseActivity: FC<{
  recommendation?: RecommendationsResponse['immerse'];
  source: ActivitySource;
  customRecommendation?: number;
}> = ({ recommendation, source, customRecommendation }) => {
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const { safePush } = useSafePush();

  const description = customRecommendation
    ? intl.formatMessage(msgs.immerseCustomRecommendation)
    : recommendation
      ? intl.formatMessage(msgs.immerseDescription)
      : intl.formatMessage(msgs.disabledImmerseDescription);

  const mediaUrl: Url = {
    pathname: ROUTES.video.media,
    query: {
      contentMediaId: customRecommendation || recommendation?.contentMediaId,
      languagePairId,
      source,
    },
  };

  return (
    <MiniPlayerContent
      icon={<S.ContentIcon name="videoLibraryFilled" $color={LIC_COLORS_DARK.immerse} />}
      title={intl.formatMessage(msgs.immerseTitle)}
      description={description}
      isEnabled={Boolean(recommendation)}
      onStart={() => safePush(mediaUrl)}
    />
  );
};

export const MiniPlayerCommunicateActivity: FC<{
  recommendation?: RecommendationsResponse['communicate'];
  source: ActivitySource;
}> = ({ recommendation, source }) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const languagePairId = getLanguagePairId();

  const description = recommendation
    ? intl.formatMessage(msgs.communicateDescription)
    : intl.formatMessage(msgs.disabledCommunicateDescription);

  const dialogUrl: Url = {
    pathname: ROUTES.membot.dialog,
    query: {
      slug: recommendation?.locationSlug,
      language_pair: languagePairId,
      source,
    },
  };

  return (
    <MiniPlayerContent
      icon={<S.ContentIcon name="communicateFilled" $color={LIC_COLORS_DARK.communicate} />}
      title={intl.formatMessage(msgs.communicateTitle)}
      description={description}
      isEnabled={Boolean(recommendation)}
      onStart={() => safePush(dialogUrl)}
    />
  );
};

const PrevArrow = ({ onClick }: Pick<DOMAttributes<HTMLButtonElement>, 'onClick'>) => {
  const intl = useIntl();
  return (
    <S.PrevArrowContainer onClick={onClick} aria-label={intl.formatMessage(msgs.back)}>
      <Icon name="displayLeft" />
    </S.PrevArrowContainer>
  );
};

const NextArrow = ({ onClick }: Pick<DOMAttributes<HTMLButtonElement>, 'onClick'>) => {
  const intl = useIntl();
  return (
    <S.NextArrowContainer onClick={onClick} aria-label={intl.formatMessage(msgs.next)}>
      <Icon name="displayRight" />
    </S.NextArrowContainer>
  );
};

export const MiniPlayer: FC<MiniPlayerProps> = ({
  currentActivity,
  onChangeActivity,
  recommendations,
  supportedActivities,
  source,
  customImmerseRecommendation,
  isLoading,
}) => {
  const intl = useIntl();
  const { asPath } = useRouter();

  const slidesOrder = useMemo(() => {
    const slides = supportedActivities;

    /* istanbul ignore next: tested visually */
    if (theme.rtl) {
      slides.reverse();
    }
    return slides as RecommendationsActivityType[];
  }, [supportedActivities]);

  const getSlideIndex = useCallback(
    (activity: RecommendationsActivityType) => slidesOrder.findIndex(a => a === activity),
    [slidesOrder],
  );

  const [displayedActivity, setDisplayedActivity] = useState(currentActivity);
  const [isSlideMoving, setIsSlideMoving] = useState(false);

  const handleBeforeChange = useCallback(() => setIsSlideMoving(true), []);

  // Unable to mock due to Slider invoking this only after the animation ends.
  // Attempts to mock time to simulate the animation causes the test to run indefinitely.
  /* istanbul ignore next */
  const handleAfterChange = useCallback(
    (currentSlide: number) => {
      setIsSlideMoving(false);
      const newActivity = slidesOrder[currentSlide];
      if (newActivity !== currentActivity) {
        setDisplayedActivity(newActivity);
        onChangeActivity(newActivity);
      }
    },
    [onChangeActivity, currentActivity, slidesOrder],
  );
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    if (displayedActivity !== currentActivity) {
      sliderRef.current?.slickGoTo(getSlideIndex(currentActivity));
    }
  }, [displayedActivity, currentActivity, getSlideIndex]);

  useEffect(() => {
    setDisplayedActivity(currentActivity);
  }, [currentActivity]);

  const nodes = {
    learn: (
      <MiniPlayerLearnActivity key="learn" recommendation={recommendations.learn} source={source} />
    ),
    immerse: (
      <MiniPlayerImmerseActivity
        key="immerse"
        recommendation={recommendations.immerse}
        source={source}
        customRecommendation={customImmerseRecommendation}
      />
    ),
    communicate: (
      <MiniPlayerCommunicateActivity
        key="communicate"
        recommendation={recommendations.communicate}
        source={source}
      />
    ),
  };

  const nodeWithSlides = (
    <S.SlidesContainer $withSlider={slidesOrder.length > 1} $isSlideMoving={isSlideMoving}>
      {slidesOrder.length > 1 ? (
        <Slider
          ref={sliderRef}
          dots
          initialSlide={getSlideIndex(currentActivity)}
          beforeChange={handleBeforeChange}
          afterChange={handleAfterChange}
          prevArrow={<PrevArrow />}
          nextArrow={<NextArrow />}
        >
          {slidesOrder.map(a => nodes[a])}
        </Slider>
      ) : (
        nodes[slidesOrder[0]]
      )}
    </S.SlidesContainer>
  );

  return (
    <S.Container $borderColor={LIC_COLORS_DARK[currentActivity]}>
      {isLoading ? (
        <S.LoadingContainer>
          <LoadingSkeletonCard data-testid="Loading-skeleton-card" $dark />
        </S.LoadingContainer>
      ) : (
        nodeWithSlides
      )}

      {asPath !== ROUTES.dashboard && (
        <S.BackToHomeSection>
          <LinkButton href={ROUTES.dashboard} buttonType="tertiary-alt" buttonSize="large">
            {intl.formatMessage(msgs.backToHomeCta)}
          </LinkButton>
        </S.BackToHomeSection>
      )}
    </S.Container>
  );
};
